import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import { useBrand } from "../context/BrandContext";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { resolvePwaBrandName } from "../pwa/applyBrandManifest";
import "./InstallAppButton.css";

const DownloadIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ChevronRight = () => (
  <svg
    className="user-action-chevron"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * Install club-branded PWA.
 * Android/Chrome: ONLY the native Install sheet (never an instruction dialog).
 * iOS: Share → Add to Home Screen (Apple has no install API).
 */
export function InstallAppButton({
  variant = "cta",
  className = "",
  hideWhenInstalled = true,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { installed, canPrompt, isIos, install } = usePwaInstall();
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waitHint, setWaitHint] = useState("");
  const appName = resolvePwaBrandName(brand);

  if (hideWhenInstalled && installed) {
    if (variant === "row") {
      return (
        <div
          className={`user-action-button-link install-app-row install-app-row--done ${className}`}
        >
          <div className="user-action-left">
            <div className="ios-setting-icon-badge install">
              <DownloadIcon />
            </div>
            <p>
              {t("installAppInstalled", "{{name}} is installed", {
                name: appName,
              })}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  const handleClick = async () => {
    if (busy) return;

    // iOS only — Apple provides no programmatic install API.
    if (isIos) {
      setHelpOpen(true);
      return;
    }

    setBusy(true);
    setWaitHint("");
    try {
      const result = await install();
      if (result === "accepted" || result === "dismissed") {
        setWaitHint("");
        return;
      }
      // Native prompt not ready yet — do NOT open a steps dialog.
      // Chrome requires ~30s on-site before beforeinstallprompt; ask user to retry.
      setWaitHint(
        t(
          "installAppWaitNative",
          "Chrome is not ready yet. Stay on this page a few more seconds, then tap Install again — the system Install sheet will open (no extra steps).",
          { name: appName }
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const label = canPrompt
    ? t("installApp", "Install {{name}} app", { name: appName })
    : t("installApp", "Install {{name}} app", { name: appName });

  const hint = waitHint
    ? waitHint
    : canPrompt
      ? t(
          "installAppHintNative",
          "Opens your browser’s Install app dialog (same as the browser menu).",
          { name: appName }
        )
      : t(
          "installAppHintWait",
          "When Chrome is ready, this button opens the Install sheet directly.",
          { name: appName }
        );

  // iOS-only instruction dialog
  const iosDialog = isIos ? (
    <Dialog
      open={helpOpen}
      onClose={() => setHelpOpen(false)}
      fullWidth
      maxWidth="xs"
      className="install-app-dialog"
      PaperProps={{
        className: "install-app-dialog__paper",
        "aria-labelledby": "install-app-dialog-title",
      }}
    >
      <div className="install-app-dialog__body">
        <h2 id="install-app-dialog-title">
          {t("installAppHowTitle", "Install {{name}}", { name: appName })}
        </h2>
        <ol className="install-app-dialog__steps">
          <li>
            {t(
              "installAppIosStep1",
              "Tap the Share button in Safari (square with an arrow)."
            )}
          </li>
          <li>
            {t(
              "installAppIosStep2",
              "Scroll and tap “Add to Home Screen”."
            )}
          </li>
          <li>{t("installAppIosStep3", "Tap Add to confirm.")}</li>
        </ol>
        <button
          type="button"
          className="install-app-dialog__close"
          onClick={() => setHelpOpen(false)}
        >
          {t("gotIt", "Got it")}
        </button>
      </div>
    </Dialog>
  ) : null;

  if (variant === "row") {
    return (
      <>
        <button
          type="button"
          className={`user-action-button-link install-app-row ${className}${
            canPrompt ? " install-app-row--ready" : ""
          }`}
          onClick={handleClick}
          disabled={busy}
        >
          <div className="user-action-left">
            <div className="ios-setting-icon-badge install">
              <DownloadIcon />
            </div>
            <div className="install-app-row__text">
              <p>{label}</p>
              <span className="install-app-row__hint">{hint}</span>
            </div>
          </div>
          <ChevronRight />
        </button>
        {iosDialog}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`install-app-cta ${className}${
          canPrompt ? " install-app-cta--ready" : ""
        }`}
        onClick={handleClick}
        disabled={busy}
      >
        <DownloadIcon />
        <span>{busy ? t("loading", "Loading…") : label}</span>
      </button>
      <p className="install-app-cta__hint">{hint}</p>
      {iosDialog}
    </>
  );
}
